import {
  Box,
  Link,
  SkeletonCircle,
  Stack,
  StackDivider,
  useColorModeValue,
  VStack,
  Wrap,
} from '@cardboard-ui/react';
import { lazy } from '@loadable/component';
import { ItemCard } from 'apps/TenantApp/components/ItemCard';
import { ItemCardSmall } from 'apps/TenantApp/components/ItemCardSmall';
import { SpaceContextCard } from 'apps/TenantApp/components/SpaceContextCard';
import { useItemViewerNavigation } from 'apps/TenantApp/screens/ItemViewer/ItemViewerNavigationProvider';
import { CardContent } from 'components/cards/Card';
import { useLastVisitedSpaceAnnouncements } from 'hooks/useLastVisited';
import React, { Suspense, useEffect } from 'react';
import { Disposable } from 'utils/graphClient';
import { RemirrorJSON } from 'remirror';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useFragment, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { useFeatureFlag } from 'utils/sessionProvider';
import {
  AnnouncementComments,
  AnnouncementCommentsSkeleton,
} from './AnnouncementComments';
import { AnnouncementMetadata } from './AnnouncementMetadata';
import { AnnouncementReactions } from './AnnouncementReactions';
import {
  AnnouncementCard_announcement$data,
  AnnouncementCard_announcement$key,
} from './__generated__/AnnouncementCard_announcement.graphql';
import { AnnouncementCard_chat_SocialsQuery } from './__generated__/AnnouncementCard_chat_SocialsQuery.graphql';
import { AnnouncementCard_chat_ThreadUpdatesSubscription } from './__generated__/AnnouncementCard_chat_ThreadUpdatesSubscription.graphql';

const RemirrorRenderer = lazy(() => import('components/Editor/Renderer'));

const announcementCardFragment = graphql`
  fragment AnnouncementCard_announcement on Announcement {
    id
    message
    space {
      id
      ...SpaceContextCard_space
    }
    links {
      nodes {
        id
        item {
          ...ItemCard_item
          ...ItemCardSmall
          ... on FolderItem {
            id
          }
          ... on AnnouncementAttachment {
            id
            content {
              ... on File {
                fileName
                mimeType
                url
              }
            }
          }
        }
      }
    }
    ...AnnouncementMetadata_announcement
  }
`;

const socialsQuery = graphql`
  query AnnouncementCard_chat_SocialsQuery($referenceId: String!) {
    socialThread(referenceId: $referenceId) {
      comments(first: 1000) @connection(key: "CommentsThread_comments") {
        edges {
          node {
            id
            text
          }
        }
      }
      reactions(first: 1000) @connection(key: "CommentsThread_reactions") {
        edges {
          node {
            id
            text
          }
        }
      }
      ...AnnouncementComments_chat_SocialThreadCommentsFragment
      ...AnnouncementReactions_chat_SocialThreadReactionsFragment
    }
  }
`;

const threadUpdatesSubscription = graphql`
  subscription AnnouncementCard_chat_ThreadUpdatesSubscription(
    $referenceId: String!
  ) {
    threadUpdates(referenceId: $referenceId) {
      comments(first: 1000) @connection(key: "CommentsThread_comments") {
        edges {
          node {
            id
            text
          }
        }
      }
      reactions(first: 1000) @connection(key: "CommentsThread_reactions") {
        edges {
          node {
            id
            text
          }
        }
      }
      ...AnnouncementComments_chat_SocialThreadCommentsFragment
      ...AnnouncementReactions_chat_SocialThreadReactionsFragment
    }
  }
`;

export interface AnnouncementCardProps {
  inPinnableContext?: boolean;
  announcement: AnnouncementCard_announcement$key;
  announcementsConnectionId: string;
  elementToPlaceRightOfSpacename?: React.ReactElement;
}

export const AnnouncementCard = ({
  inPinnableContext,
  announcement,
  announcementsConnectionId,
  elementToPlaceRightOfSpacename,
}: AnnouncementCardProps) => {
  const shouldShowPrettyAnnouncements = useFeatureFlag(
    'FEATURE_PRETTY_ANNOUNCEMENTS',
  );
  const hasSocials = useFeatureFlag('FEATURE_COMMENTS_ON_ANNOUNCEMENTS');

  const { id, message, space, links, ...otherRefs } = useFragment(
    announcementCardFragment,
    announcement,
  );

  const { registerVisit } = useLastVisitedSpaceAnnouncements(space.id);

  useEffect(() => {
    registerVisit();
  }, []);

  if (!message) {
    return null;
  }

  return (
    <SpaceContextCard
      space={space}
      isNotClickable
      data-qa="announcement-card"
      elementToPlaceRightOfSpacename={elementToPlaceRightOfSpacename}
    >
      <Stack>
        <CardContent m={2}>
          <Stack spacing={5}>
            <AnnouncementMetadata
              inPinnableContext={inPinnableContext}
              announcement={otherRefs}
              announcementsConnectionId={announcementsConnectionId}
            />
            {shouldShowPrettyAnnouncements ? (
              <RemirrorRenderer json={message as RemirrorJSON} />
            ) : (
              <Stack px={{ base: 1, md: 12 }} spacing={5} py={2}>
                <RemirrorRenderer json={message as RemirrorJSON} />
                <Links links={links} />
              </Stack>
            )}
            {shouldShowPrettyAnnouncements && <Links links={links} />}

            {hasSocials && (
              <ErrorBoundary>
                <Suspense fallback={<AnnouncementSocialsSuspended />}>
                  <AnnouncementSocials spaceId={space.id} announcementId={id} />
                </Suspense>
              </ErrorBoundary>
            )}
          </Stack>
        </CardContent>
      </Stack>
    </SpaceContextCard>
  );
};

const AnnouncementSocialsSuspended = () => {
  const isFeatureReactionsOnAnnouncementsEnabled = useFeatureFlag(
    'FEATURE_REACTIONS_ON_ANNOUNCEMENTS',
  );

  return (
    <Stack spacing={4} divider={<StackDivider />}>
      {isFeatureReactionsOnAnnouncementsEnabled && (
        <Wrap spacing={1} data-qa="message-reactions" justify="end">
          <SkeletonCircle width={6} height={6} />
        </Wrap>
      )}
      <AnnouncementCommentsSkeleton />
    </Stack>
  );
};

const AnnouncementSocials = ({
  announcementId,
  spaceId,
}: {
  announcementId: string;
  spaceId: string;
}) => {
  const isFeatureReactionsOnAnnouncementsEnabled = useFeatureFlag(
    'FEATURE_REACTIONS_ON_ANNOUNCEMENTS',
  );
  const isFeatureSEESubscriptionsEnabled = useFeatureFlag(
    'FEATURE_SSE_SUBSCRIPTIONS_PROTOCOL',
  );
  const { useLazyLoadQuery, requestSubscription } = useSafeRelay();

  const { socialThread } = useLazyLoadQuery<AnnouncementCard_chat_SocialsQuery>(
    socialsQuery,
    {
      referenceId: announcementId,
    },
  );

  useEffect(() => {
    let threadUpdatesSubscriptionDisposable: Disposable;

    threadUpdatesSubscriptionDisposable =
      requestSubscription<AnnouncementCard_chat_ThreadUpdatesSubscription>(
        threadUpdatesSubscription,
        {
          referenceId: announcementId,
        },
      );

    return () => {
      threadUpdatesSubscriptionDisposable?.dispose();
    };
  }, [isFeatureReactionsOnAnnouncementsEnabled]);

  if (!socialThread) {
    return null;
  }

  return (
    <Stack spacing={4} divider={<StackDivider />}>
      {isFeatureReactionsOnAnnouncementsEnabled && (
        <AnnouncementReactions
          socialThread={socialThread}
          announcementId={announcementId}
        />
      )}
      <AnnouncementComments
        announcementId={announcementId}
        spaceId={spaceId}
        socialThread={socialThread}
      />
    </Stack>
  );
};

const Masonry = lazy(() => import('react-responsive-masonry'));

const Links = ({
  links,
}: {
  links: AnnouncementCard_announcement$data['links'];
}) => {
  const { setItemViewerCollectionIds } = useItemViewerNavigation();
  const shouldShowPrettyAnnouncements = useFeatureFlag(
    'FEATURE_PRETTY_ANNOUNCEMENTS',
  );
  const itemNodes = links.nodes?.map((n) => n).filter(notEmpty) || [];
  const thumbnailUrls = itemNodes
    .map((n) =>
      (n?.item?.content?.mimeType || '').startsWith('image/')
        ? {
            url: n?.item?.content?.url as string,
            id: n.item?.id as string,
            fileName: n?.item?.content?.fileName as string,
          }
        : null,
    )
    .filter(notEmpty);

  const setItemViewerCollection = () => {
    setItemViewerCollectionIds(
      itemNodes.map(({ item, id }) => item?.id).filter(notEmpty),
    );
  };

  const bgColor = useColorModeValue('white', 'gray.700');

  if (itemNodes.length === 0) {
    return null;
  }

  if (
    thumbnailUrls.length === itemNodes.length &&
    itemNodes.length === 1 &&
    shouldShowPrettyAnnouncements
  ) {
    const singleThumbnail = thumbnailUrls[0];
    return (
      <Link toItemViewer={singleThumbnail.id}>
        <img src={singleThumbnail.url} alt={singleThumbnail.fileName} />
      </Link>
    );
  }

  if (
    thumbnailUrls.length === itemNodes.length &&
    shouldShowPrettyAnnouncements
  ) {
    return (
      <Box
        onClick={setItemViewerCollection}
        style={{
          // Background style generated with https://www.magicpattern.design/tools/css-backgrounds
          backgroundSize: '7px 7px',
          backgroundImage:
            'repeating-linear-gradient(45deg, #ddd 0, #ddd 0.7000000000000001px, transparent 0, transparent 50%)',
        }}
      >
        <Masonry columnsCount={2} gutter="0px">
          {thumbnailUrls.map(({ url, id, fileName }) => (
            <Link
              key={id}
              toItemViewer={id}
              borderWidth={1}
              borderColor={bgColor}
            >
              <img src={url} alt={fileName} loading="lazy" />
            </Link>
          ))}
        </Masonry>
      </Box>
    );
  }

  if (itemNodes.length === 1 && itemNodes[0].item) {
    return (
      <Box px={shouldShowPrettyAnnouncements ? 2 : 0}>
        <ItemCard item={itemNodes[0].item} />
      </Box>
    );
  }

  return (
    <VStack
      alignItems="stretch"
      px={shouldShowPrettyAnnouncements ? 2 : 0}
      onClick={setItemViewerCollection}
    >
      {itemNodes.map(({ id, item }) => (
        <ItemCardSmall key={id} item={item} hideSpace />
      ))}
    </VStack>
  );
};
